import { EncryptionMethod, encryptionMethodOptions, SSLMode, sslModeOptions } from '../consts/postgre-consts';
import { AzurePostgreSQLSink } from 'app/model/pipeline/copysink/azure-postgre-sql.copysink';
import { AzurePostgreSqlCopySource } from 'app/model/pipeline/copysource/azure-postgresql.copysource';
import { ILinkedServiceTypeMetadata, IDynamicLinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';
import { Metamodel } from 'app/model/base/model';
import { authenticationReferenceMethodOptions, azureCloudTypeMetadata, azureResourceSelectMethodOptions } from '../consts/metadata-consts';
import { format } from 'app/shared/utils/string.util';
import { AuthConstants } from 'app/services/authentication/auth-constants';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { DatasetFormatType } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { IDataflowBuildInTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataflow-format-type-metadata';
import { azureDefaultAuthRefTypes, azurePostgreSqlAuthTypes, ConnectionAuthKind, CredentialTypeOptions, Type } from 'shell-models-lib';
import { DataSourceOptionKind, CommonResourceKind as ResourceKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';
import { type ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { driverVersions } from '../consts/tabular-consts';
import { servicePrincipalCredentialTypes } from 'app/model/linkedservice/dynamics.linkedservice';
import { azPgSqlAdditionalFieldsKeyOptions } from 'app/model/adapters/linkedservices/azure-db-postgre-linkedservice.adapter';

export const azurePostgrelSqlDomain: string = AuthConstants.RESOURCE_DOMAIN_MAP[adfModelTypeMap.azurePostgreSQL.linkedServiceType];
export const additionalPropertiesInFabricForPostgreSql = [
    {
        label: 'Schema',
        value: 'schema',
        type: Type.String
    },
    {
        label: 'Pooling',
        value: 'pooling',
        type: Type.Boolean
    },
    {
        label: 'Connection timeout',
        value: 'connectionTimeout',
        type: Type.Int
    },
    {
        label: 'Command timeout',
        value: 'commandTimeout',
        type: Type.Int
    },
    {
        label: 'Trust server certificate',
        value: 'trustServerCertificate',
        type: Type.Boolean
    },
    {
        label: 'Read buffer size',
        value: 'readBufferSize',
        type: Type.Int
    },
    {
        label: 'Timezone',
        value: 'timezone',
        type: Type.String
    },
    {
        label: 'Encoding',
        value: 'encoding',
        type: Type.String
    }
];
const azurePostgreSqlResourceTypes: string[] = ['Microsoft.DBforPostgreSQL/servers', 'Microsoft.DBforPostgreSQL/flexibleServers', 'Microsoft.DBforPostgreSQL/serverGroupsv2'];

const dataflowAzurePostgreSql: IDataflowBuildInTypeMetadata = {
    type: DatasetFormatType.PostgreSql,
    settings: adfModelTypeMap.azurePostgreSQL.dataflowSettings,
    enableImportSchemaInSink: true,
    enableAlterRow: true
};

const basicDBConfigControl: ControlMetadata = {
    type: 'radio-switch',
    nested: 1,
    label: stringResources.AccountSelectionMethodLabel,
    description: stringResources.AzureSql_SelectMethodDescription,
    options: azureResourceSelectMethodOptions,
    defaultValue: '{serverName != null || parameterized ? "manual" : "azure"}',
    isLabelVertical: true,
    cases: [
        {
            caseValue: 'azure',
            children: [
                {
                    type: 'azure-server-database-select',
                    bind: {
                        serverName: 'serverName',
                        port: 'port',
                        databaseName: 'databaseName',
                        resourceID: '_serverResourceID'
                    },
                    showPEStatus: true,
                    defaultPort: '5432',
                    resourceTypes: azurePostgreSqlResourceTypes,
                    resourceTypeLabels: {
                        servers: stringResources.SingleServer,
                        flexibleServers: stringResources.FlexibleServer,
                        serverGroupsv2: stringResources.Hyperscale
                    },
                    serverDomain: azurePostgrelSqlDomain,
                    validators: {
                        serverName: [
                            { type: 'required', errorMessage: '' }
                        ],
                        databaseName: [
                            { type: 'required', errorMessage: '' }
                        ]
                    }
                }
            ]
        },
        {
            caseValue: 'manual',
            children: [
                {
                    type: 'parameterizable', child: {
                        type: 'textbox',
                        bind: 'serverName',
                        label: stringResources.FullyServerNameLabel,
                        required: true,
                        placeholder: `e.g., myserver.${azurePostgrelSqlDomain}`,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'parameterizable', child: {
                        type: 'textbox',
                        bind: 'port',
                        label: stringResources.ModelName_Port,
                        isLabelVertical: true,
                        inputType: 'number',
                        min: 0
                    }
                },
                {
                    type: 'parameterizable', child: {
                        type: 'textbox',
                        bind: 'databaseName',
                        label: stringResources.ModelName_DatabaseName,
                        required: true,
                        validators: [
                            { type: 'required', errorMessage: '' }
                        ],
                        isLabelVertical: true
                    }
                },
                {
                    type: 'private-endpoint-status'
                }
            ]
        }
    ]
}

const connectionStringControls: ControlMetadata[] = [
    basicDBConfigControl,
    {
        type: 'parameterizable', child: {
            type: 'textbox',
            bind: 'userName',
            label: stringResources.ModelName_UserName,
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        }
    },
    {
        type: 'dynamic-akv-switcher',
        enableAKVParameterization: true,
        hideAKVSwitcherInConnStrProps: true,
        bind: 'password',
        label: stringResources.ModelName_Password,
        child: {
            type: 'parameterizable',
            attribute: 'password',
            disableAddParameter: true,
            child: {
                type: 'password',
                label: stringResources.ModelName_Password,
                required: true,
                isLabelVertical: true,
                validators: [
                    { type: 'required', errorMessage: '' }
                ],
            },
        },
        description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
            format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzurePostgreSql_DisplayText),
            stringResources.ModelName_Password)
    },

];

const authenticationTypeOptions = [
    {
        label: stringResources.BasicAuth,
        value: azurePostgreSqlAuthTypes.Basic
    },
    {
        label: stringResources.AuthenticationType_ServicePrincipal,
        value: azurePostgreSqlAuthTypes.ServicePrincipal
    },
    {
        label: stringResources.AuthenticationType_ManagedServiceIdentity,
        value: azurePostgreSqlAuthTypes.SystemAssignedManagedIdentity
    },
    {
        label: stringResources.AuthenticationType_UserAssignedManagedIdentity,
        value: azurePostgreSqlAuthTypes.UserAssignedManagedIdentity
    }
];


const tenantInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'tenant-textbox',
        bind: 'tenant',
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true,
        label: stringResources.ModelName_Tenant,
        required: true
    }
};

const servicePrincipalIdInput: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'textbox',
        bind: 'servicePrincipalId',
        label: stringResources.ModelName_ServicePrincipalID,
        required: true,
        validators: [{ type: 'required', errorMessage: '' }],
        isLabelVertical: true
    }
};

const servicePrincipalKeyControl: ControlMetadata = {
    type: 'dynamic-akv-switcher',
    bind: 'servicePrincipalKey',
    enableAKVParameterization: true,
    label: stringResources.ModelName_ServicePrincipalKey,
    child: {
        type: 'parameterizable',
        attribute: 'servicePrincipalKey',
        disableAddParameter: true,
        child: {
            type: 'password',
            label: stringResources.ModelName_ServicePrincipalKey,
            required: true,
            validators: [{ type: 'required', errorMessage: '' }],
            isLabelVertical: true
        }
    },
    description: format(
        stringResources.AzureKeyVaultSecretName_Tooltip_Template,
        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzurePostgreSql_DisplayText),
        stringResources.ModelName_ServicePrincipalKey
    )
};

const spSelector: ControlMetadata = {
    type: 'radio-switch',
    bind: 'servicePrincipalCredentialType',
    label: stringResources.ModelName_ServicePrincipalCredentialType,
    isLabelVertical: true,
    nested: 1,
    options: [
        {
            label: stringResources.ModelName_ServicePrincipalCert,
            value: servicePrincipalCredentialTypes.ServicePrincipalCert
        },
        {
            label: stringResources.ModelName_ServicePrincipalKey,
            value: servicePrincipalCredentialTypes.ServicePrincipalKey
        }
    ],
    required: true,
    validators: [{ type: 'required', errorMessage: '' }],
    defaultValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
    cases: [
        {
            caseValue: servicePrincipalCredentialTypes.ServicePrincipalKey,
            children: [
                servicePrincipalKeyControl
            ]
        },
        {
            caseValue: servicePrincipalCredentialTypes.ServicePrincipalCert,
            children: [
                {
                    type: 'dynamic-akv-switcher',
                    bind: 'servicePrincipalEmbeddedCert',
                    enableAKVParameterization: true,
                    label: stringResources.ModelName_ServicePrincipalCert,
                    child: {
                        type: 'parameterizable',
                        attribute: 'servicePrincipalEmbeddedCert',
                        disableAddParameter: true,
                        child: {
                            type: 'key-file-content',
                            label: stringResources.ModelName_ServicePrincipalCert,
                            required: true,
                            isLabelVertical: true,
                            validators: [{ type: 'required', errorMessage: '' }],
                        },
                    },
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzurePostgreSql_DisplayText),
                        stringResources.ModelName_ServicePrincipalCert)
                },
                {
                    type: 'dynamic-akv-switcher',
                    enableAKVParameterization: true,
                    bind: 'servicePrincipalEmbeddedCertPassword',
                    label: stringResources.ModelName_ServicePrincipalCertPassword,
                    child: {
                        type: 'parameterizable',
                        attribute: 'servicePrincipalEmbeddedCertPassword',
                        disableAddParameter: true,
                        child: {
                            type: 'password',
                            label: stringResources.ModelName_ServicePrincipalCertPassword,
                            isLabelVertical: true
                        }
                    },
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzurePostgreSql_DisplayText),
                        stringResources.ModelName_ServicePrincipalCertPassword)
                },

            ]
        }
    ]
};

// provides user with inline vs credential radio button to add authentication parameters for SAMI and SP Authentication methods.
function azurePGSqlAuthReferenceSetting(credentialTypeFilter: CredentialTypeOptions): ControlMetadata {
    if (credentialTypeFilter === CredentialTypeOptions.SystemAssignedManagedIdentity && !GlobalVariables.enableSamiSupport) {
        return {
            type: 'management-identity-info',
            resourceType: stringResources.AzurePostgreSql_DisplayText,
            link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-database-for-postgresql?tabs=data-factory#managed-identity'
        }
    }
    return {
        type: 'radio-switch',
        bind: 'authReferenceMethod',
        nested: 1,
        label: stringResources.Authentication_Reference_Method_Label,
        description: stringResources.Authentication_Reference_Method_Description,
        options: authenticationReferenceMethodOptions,
        isLabelVertical: true,
        defaultValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
        cases: [
            {
                caseValue: azureDefaultAuthRefTypes.InlineRefTypeAuthentication,
                children:
                    credentialTypeFilter === CredentialTypeOptions.ServicePrincipal
                        ? [
                            // service Principal
                            tenantInput,
                            servicePrincipalIdInput,
                            spSelector,
                            azureCloudTypeMetadata
                        ]
                        : [
                            // system Assigned Managed Identity
                            {
                                type: 'management-identity-info',
                                resourceType: stringResources.AzurePostgreSql_DisplayText,
                                link: 'https://learn.microsoft.com/azure/data-factory/connector-azure-database-for-postgresql?tabs=data-factory#managed-identity'
                            }
                        ]
            },
            {
                caseValue: azureDefaultAuthRefTypes.CredentialRefTypeAuthentication,
                children: [
                    {
                        type: 'credential-select',
                        bind: 'credential',
                        filterCredentialType: credentialTypeFilter,
                        required: true,
                        validators: [{ type: 'required', errorMessage: '' }]
                    }
                ]
            }
        ]
    };
}

const authenticationTypeControl: ControlMetadata = {
    type: 'select-switch',
    bind: 'authenticationType',
    label: stringResources.ModelName_AuthenticationType,
    options: authenticationTypeOptions,
    isLabelVertical: true,
    defaultValue: azurePostgreSqlAuthTypes.Basic,
    cases: [
        {
            caseValue: azurePostgreSqlAuthTypes.Basic,
            children: connectionStringControls
        },
        {
            caseValue: azurePostgreSqlAuthTypes.ServicePrincipal,
            children: [basicDBConfigControl, {
                type: 'parameterizable', child: {
                    type: 'textbox',
                    bind: 'userName',
                    label: stringResources.ModelName_ServicePrincipalName,
                    required: true,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ],
                    isLabelVertical: true
                }
            }, azurePGSqlAuthReferenceSetting(CredentialTypeOptions.ServicePrincipal)]
        },
        {
            caseValue: azurePostgreSqlAuthTypes.SystemAssignedManagedIdentity,
            children: [basicDBConfigControl, azurePGSqlAuthReferenceSetting(CredentialTypeOptions.SystemAssignedManagedIdentity)]
        },
        {
            caseValue: azurePostgreSqlAuthTypes.UserAssignedManagedIdentity,
            children: [
                basicDBConfigControl,
                {
                    type: 'credential-select',
                    bind: 'credential',
                    filterCredentialType: CredentialTypeOptions.UserAssignedManagedIdentity,
                    required: true,
                    validators: [{ type: 'required', errorMessage: '' }]
                }
            ]
        }
    ]
};


export const getControlsByVersion = (version: driverVersions): ControlMetadata[] => {
    return version === driverVersions.v2
        ? [
            authenticationTypeControl,
            {
                type: 'select-switch',
                bind: 'sslMode',
                label: stringResources.SSL_Mode,
                isLabelVertical: true,
                options: sslModeOptions,
                defaultValue: SSLMode.Perfer
            },
            {
                type: 'parameter-config',
                bind: 'parameterizableAdditionalFields',
                label: stringResources.AdditionalConnectionStringFields,
                isLayoutVertical: true,
                isKeyDropdown: true,
                keyOptions: azPgSqlAdditionalFieldsKeyOptions,
                keyOptionHasType: true,
                dynamicInputTypes: true,
                dynamicInputTypesParameterizable: true,
                keyLabel: stringResources.PropertyName,
                defaultWidth: 250
            }
        ]
        : connectionStringControls.concat([
            {
                type: 'select-switch',
                bind: 'encryptionMethod',
                label: stringResources.Postgresql_Encryption_Method,
                isLabelVertical: true,
                options: encryptionMethodOptions,
                defaultValue: EncryptionMethod.NoEncryption,
                cases: [
                    {
                        caseValue: EncryptionMethod.NoEncryption,
                        children: []
                    },
                    {
                        caseValue: null,
                        children: [
                            {
                                type: 'radio-switch',
                                bind: 'validateServerCertificate',
                                isLabelVertical: true,
                                options: [
                                    {
                                        label: stringResources.Postgresql_Validate_Server_Certificate,
                                        value: true
                                    },
                                    {
                                        label: stringResources.Postgresql_Not_Validate_Server_Certificate,
                                        value: false
                                    }
                                ],
                                defaultValue: false
                            }
                        ]
                    }
                ]
            },
            {
                type: 'key-val-list',
                bind: 'additionalFields',
                label: stringResources.AdditionalConnectionStringFields
            }]);
};

const v1Controls: ControlMetadata = {
    type: 'select-button-switch',
    required: true,
    isLabelVertical: true,
    options: [
        {
            label: stringResources.ModelName_ConnectionString,
            value: false
        },
        {
            label: stringResources.AzureKeyVault_DisplayText,
            value: true,
            hideAKVOptionIfNotChosen: false
        }
    ],
    bind: 'enableAKV',
    isNotRelatedToCredential: true,
    defaultValue: false,
    cases: [
        {
            caseValue: true,
            children: [
                {
                    enableAKVParameterization: true,
                    type: 'azure-key-vault',
                    bind: 'referedAKV',
                    isLabelVertical: true,
                    description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                        format(stringResources.ModelName_LinkedServiceTemplate, stringResources.AzurePostgreSql_DisplayText),
                        `Server=<server>.${azurePostgrelSqlDomain};Database=<database>;Port=<port>;UID=<username>@admstest;Password=<Password>`)
                }
            ]
        },
        {
            caseValue: false,
            children: getControlsByVersion(driverVersions.v1)
        }
    ]
}

const versionSelectControl: ControlMetadata = {
    type: 'radio-switch',
    bind: 'version',
    label: stringResources.VersionLabel,
    isLabelVertical: true,
    options: [
        {
            label: '2.0',
            value: driverVersions.v2,
            description: stringResources.AzurePostgreSql_Recommended_Version_Description
        },
        {
            label: '1.0',
            value: driverVersions.v1
        }
    ],
    defaultValue: driverVersions.v2,
    cases: [
        {
            caseValue: driverVersions.v2,
            children: getControlsByVersion(driverVersions.v2)
        },
        {
            caseValue: driverVersions.v1,
            children: [v1Controls]
        }
    ]
};

export const azurePostgreSqlMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.azurePostgreSQL.linkedServiceType,
        displayName: stringResources.AzurePostgreSql_DisplayText,
        linkedServiceKind: 'table',
        supportedInTrident: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.Extension,
            resourceKind: 'AzurePostgreSQL' as ResourceKind
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic, ConnectionAuthKind.OAuth2],
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        supportOPDGInMirror: GlobalVariables.mirrorOPDGAzPgSql,
        supportVNETInMirror: true,
        icon: {
            className: 'svg-i-100x100-azure_postgreSQL'
        },
        isFirstClass: true,
        supportDataFlowSource: true,
        supportDataFlowSink: true,
        supportFirstClassParameterExperience: true,
        supportScriptActivity: true,
        dataflowSource: [dataflowAzurePostgreSql],
        dataflowSink: [dataflowAzurePostgreSql],
        dataflowSinkStagingSchema: true,
        dataFlowSinkUseDefaultStagingSchemaLabel: stringResources.UseSinkSchema,
        dataFlowSinkUseDefaultStagingSchemaDescription: stringResources.CustomizedStagingSchemaDescription,
        supportPE: true,
        peGroupIDs: ['postgresqlServer'],
        resourceTypes: azurePostgreSqlResourceTypes,
        peWellknowErrorMessages: [
            /The public network access on this server is disabled. To connect to this server, use the Private Endpoint from inside your virtual network/i
        ],
        copySinkTypes: [Metamodel.modelFor(AzurePostgreSQLSink).name],
        supportDatasetV2: true,
        tableColumnNameQuotes: ['"', '"'],
        supportPreCopyScript: true,
        copySourceTypes: [Metamodel.modelFor(AzurePostgreSqlCopySource).name],
        category: ['Azure'],
        SHIRVersion: {
            v2: '5.48.0'
        },
        controls: [
            versionSelectControl
        ],
        additionalPropertiesInFabric: GlobalVariables.connectionPropertiesForPostgreSql ? additionalPropertiesInFabricForPostgreSql : undefined
    };
    return metadata;
}

